import { cn } from "@/lib/cn";

/**
 * Ambient light field.
 *
 * Slow, low-contrast emerald and lavender blooms behind content. Purely
 * decorative: `aria-hidden`, pointer-events off, and reduced to a static
 * gradient when the visitor prefers reduced motion (handled globally in
 * `styles/index.css`).
 *
 * Deliberately not neon — opacity stays low and the blur radius stays large so
 * these read as depth rather than as glow effects.
 */
export function AuroraField({
  className,
  variant = "hero",
}: {
  className?: string;
  variant?: "hero" | "soft" | "edge";
}) {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden",
        className,
      )}
    >
      {variant === "hero" ? (
        <>
          <div className="aurora absolute -top-[22%] left-1/2 h-[38rem] w-[46rem] -translate-x-1/2 rounded-full bg-emerald-600/18 blur-[130px]" />
          <div className="aurora-slow absolute top-[8%] -left-[12%] h-[30rem] w-[34rem] rounded-full bg-emerald-500/12 blur-[140px]" />
          <div className="aurora-slow absolute -right-[10%] bottom-[2%] h-[26rem] w-[30rem] rounded-full bg-lavender-500/12 blur-[150px]" />
        </>
      ) : null}

      {variant === "soft" ? (
        <>
          <div className="aurora-slow absolute top-[10%] -left-[15%] h-[26rem] w-[30rem] rounded-full bg-emerald-700/14 blur-[150px]" />
          <div className="aurora-slow absolute -right-[12%] bottom-[6%] h-[24rem] w-[28rem] rounded-full bg-lavender-500/10 blur-[150px]" />
        </>
      ) : null}

      {variant === "edge" ? (
        <div className="aurora absolute -bottom-[28%] left-1/2 h-[32rem] w-[54rem] -translate-x-1/2 rounded-full bg-emerald-600/16 blur-[140px]" />
      ) : null}
    </div>
  );
}

/**
 * A faint grid that gives large dark areas a sense of scale. Fades out towards
 * the edges so it never reads as a hard texture.
 */
export function GridField({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0",
        "[background-image:linear-gradient(to_right,rgba(244,241,234,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(244,241,234,0.04)_1px,transparent_1px)]",
        "[background-size:64px_64px]",
        "[mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_72%)]",
        className,
      )}
    />
  );
}
